import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  Pencil,
  Save,
  Loader2,
  ArrowLeft,
  ChevronDown,
  TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import GlassCard from "@/components/GlassCard";
import GradientMesh from "@/components/GradientMesh";
import ContentRestrictionsSection from "@/components/ContentRestrictionsSection";
import EmailNotificationsSection from "@/components/EmailNotificationsSection";

/**
 * BusinessProfile page
 * --------------------
 * Dedicated page for editing the business profile. The exact same fields,
 * placeholders, testids, and save behavior that previously lived inline
 * on the Dashboard — moved here 1:1. The AI generation pipeline consumes
 * this profile via GET /api/profile without any change on the backend.
 *
 * IMPORTANT: this is a pure frontend/navigation relocation. Every existing
 * profile field is preserved. No schema change. Save handler goes to the
 * same PUT /api/profile endpoint.
 */
const BUSINESS_TYPES = [
  "Restaurant",
  "Cafe",
  "Salon / Beauty",
  "Fitness Studio",
  "Real Estate",
  "Retail Store",
  "Health & Wellness",
  "Professional Services",
  "Other",
];
const TONES = ["Friendly", "Professional", "Casual", "Bold", "Playful", "Luxurious", "Local / Community", "Promotional"];
const FREQUENCIES = ["Daily", "3x/week", "Weekly", "Monthly", "Decide later"];
const PLATFORMS = ["Instagram", "Facebook", "X / Twitter", "LinkedIn"];

export default function BusinessProfilePage() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showOptionalDetails, setShowOptionalDetails] = useState(false);
  const [loading, setLoading] = useState(true);
  // Personal (owner-side) fields — separate from business profile.
  // Editing your first name must NOT touch business_name and vice versa.
  const [firstName, setFirstName] = useState("");
  const [savingFirstName, setSavingFirstName] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/profile");
        if (!cancelled) setProfile(res.data);
      } catch {
        if (!cancelled) toast.error("Could not load your profile");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Seed the first-name input from the authenticated user record. This
  // is intentionally decoupled from `profile` — the field lives on the
  // user document, not on business_profiles.
  useEffect(() => {
    if (user && typeof user.first_name === "string") {
      setFirstName(user.first_name);
    }
  }, [user]);

  // Business Type "Other" support: when the user picks Other, a small
  // text field reveals so they can supply their actual industry
  // (e.g. "Pet grooming"). Backend already resolves the AI context
  // preferentially from `business_type_custom` when
  // `business_type == "Other"` (see server.py _build_context).
  const BUSINESS_TYPE_CUSTOM_MAX = 30;
  const isOtherBusinessType =
    (profile?.business_type || "").toLowerCase() === "other";
  const customBusinessTypeValue = profile?.business_type_custom || "";
  const otherRequiresText =
    isOtherBusinessType && !customBusinessTypeValue.trim();

  const profileComplete = useMemo(
    () =>
      !!(
        profile &&
        profile.business_name &&
        profile.business_type &&
        !(
          (profile.business_type || "").toLowerCase() === "other" &&
          !((profile.business_type_custom || "").trim())
        )
      ),
    [profile],
  );

  const onSaveProfile = async () => {
    // Server accepts business_type_custom; enforce the same
    // client-side invariants used by the completeness check so the
    // Save button never emits an incomplete Other selection.
    if (otherRequiresText) {
      toast.error("Please describe your business type before saving.");
      return;
    }
    setSaving(true);
    try {
      // Trim + cap custom value. When the user is NOT on "Other", we
      // clear business_type_custom so the AI context never keeps a
      // stale custom label from a previous selection.
      const payload = { ...profile };
      if (isOtherBusinessType) {
        payload.business_type_custom = customBusinessTypeValue
          .trim()
          .slice(0, BUSINESS_TYPE_CUSTOM_MAX);
      } else {
        payload.business_type_custom = "";
      }
      const res = await api.put("/profile", payload);
      setProfile(res.data);
      setEditing(false);
      toast.success("Business profile saved");
    } catch {
      toast.error("Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  const onSaveFirstName = async () => {
    const trimmed = (firstName || "").trim();
    setSavingFirstName(true);
    try {
      const res = await api.put("/users/me", { first_name: trimmed });
      // Keep the auth context in sync so the Dashboard greeting reflects
      // the change immediately, without a hard reload.
      if (res.data) {
        setUser((prev) => (prev ? { ...prev, ...res.data } : res.data));
      }
      toast.success("Name saved");
    } catch {
      toast.error("Could not save your name");
    } finally {
      setSavingFirstName(false);
    }
  };

  const togglePlatform = (p) => {
    const current = profile?.platforms || [];
    const next = current.includes(p) ? current.filter((x) => x !== p) : [...current, p];
    setProfile({ ...profile, platforms: next });
  };

  return (
    <div className="relative">
      <GradientMesh />
      <div className="relative">
        <PageHeader
          title="Business profile"
          subtitle="Your brand voice powers every AI-generated post. The more accurate this is, the better Postmoro sounds like you."
          testidPrefix="business-profile"
          action={
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => navigate("/dashboard")}
                data-testid="business-profile-back"
                className="text-[color:var(--text-2)] hover:text-[color:var(--text-1)]"
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Back to dashboard
              </Button>
            </div>
          }
        />

        <GlassCard className="p-6 mb-4" delay={0.02} data-testid="user-personal-card">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="h-9 w-9 rounded-md bg-[color:rgba(27,168,159,0.12)] text-[color:var(--brand-primary)] flex items-center justify-center shrink-0 mt-0.5">
              <Pencil className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="eyebrow">About you</div>
              <div className="font-display text-lg font-semibold text-[color:var(--text-1)]">
                Your first name
              </div>
              <div className="text-xs text-[color:var(--text-3)] mt-0.5">
                Only used to greet you inside Postmoro. Never inserted into your posts. Change any time.
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="e.g. Justin"
                  data-testid="user-first-name-input"
                  maxLength={60}
                  className="max-w-xs bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                />
                <Button
                  onClick={onSaveFirstName}
                  disabled={savingFirstName || (firstName.trim() === ((user?.first_name || "").trim()))}
                  className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)]"
                  data-testid="user-first-name-save"
                >
                  {savingFirstName ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                  Save
                </Button>
              </div>
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-6" delay={0.05} data-testid="business-profile-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="eyebrow">Business profile</div>
              <div className="font-display text-xl font-semibold">Your brand voice</div>
            </div>
            {!editing ? (
              <Button
                variant="ghost"
                onClick={() => setEditing(true)}
                className="text-[color:var(--text-2)] hover:text-[color:var(--text-1)]"
                data-testid="profile-edit-button"
              >
                <Pencil className="h-4 w-4 mr-1" />Edit
              </Button>
            ) : (
              <Button
                onClick={onSaveProfile}
                disabled={saving || otherRequiresText}
                title={otherRequiresText ? "Please describe your business type before saving." : undefined}
                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)]"
                data-testid="profile-save-button"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                Save
              </Button>
            )}
          </div>

          {loading || !profile ? (
            <div className="text-sm text-[color:var(--text-3)]">Loading…</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-[color:var(--text-2)]">Business name</label>
                <Input
                  value={profile.business_name || ""}
                  disabled={!editing}
                  onChange={(e) => setProfile({ ...profile, business_name: e.target.value })}
                  placeholder="e.g. Bella Pizza"
                  data-testid="profile-business-name"
                  className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                />
              </div>
              <div>
                <label className="text-xs text-[color:var(--text-2)]">Location</label>
                <Input
                  value={profile.location || ""}
                  disabled={!editing}
                  onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                  placeholder="Austin, TX"
                  data-testid="profile-location"
                  className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                />
              </div>
              <div>
                <label className="text-xs text-[color:var(--text-2)]">Business type</label>
                <Select
                  value={profile.business_type || ""}
                  disabled={!editing}
                  onValueChange={(v) => {
                    // Clear stale custom text when switching AWAY
                    // from Other so a previously-saved custom label
                    // never influences AI context after a category
                    // switch.
                    const next = { ...profile, business_type: v };
                    if ((v || "").toLowerCase() !== "other") {
                      next.business_type_custom = "";
                    }
                    setProfile(next);
                  }}
                >
                  <SelectTrigger className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]" data-testid="profile-business-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUSINESS_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isOtherBusinessType ? (
                  <div className="mt-2" data-testid="profile-business-type-custom-wrap">
                    <div className="flex items-center justify-between">
                      <label className="text-xs text-[color:var(--text-2)]">Business type</label>
                      <span
                        className="text-[11px] text-[color:var(--text-3)]"
                        data-testid="profile-business-type-custom-counter"
                      >
                        {customBusinessTypeValue.length}/{BUSINESS_TYPE_CUSTOM_MAX}
                      </span>
                    </div>
                    <Input
                      value={customBusinessTypeValue}
                      disabled={!editing}
                      onChange={(e) =>
                        setProfile({
                          ...profile,
                          business_type_custom: e.target.value.slice(
                            0,
                            BUSINESS_TYPE_CUSTOM_MAX,
                          ),
                        })
                      }
                      placeholder="e.g. Pet grooming"
                      maxLength={BUSINESS_TYPE_CUSTOM_MAX}
                      data-testid="profile-business-type-custom"
                      className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                    />
                    {editing && otherRequiresText ? (
                      <p
                        className="mt-1 text-[11px] text-[color:var(--danger)]"
                        data-testid="profile-business-type-custom-error"
                      >
                        Please describe your business type.
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div>
                <label className="text-xs text-[color:var(--text-2)]">Tone / style</label>
                <Select value={profile.tone || "Friendly"} disabled={!editing} onValueChange={(v) => setProfile({ ...profile, tone: v })}>
                  <SelectTrigger className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]" data-testid="profile-tone">
                    <SelectValue placeholder="Select tone" />
                  </SelectTrigger>
                  <SelectContent>
                    {TONES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[color:var(--text-2)]">Posting frequency</label>
                <Select value={profile.frequency || "3x/week"} disabled={!editing} onValueChange={(v) => setProfile({ ...profile, frequency: v })}>
                  <SelectTrigger className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]" data-testid="profile-frequency">
                    <SelectValue placeholder="How often" />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {profile.frequency === "Decide later" ? (
                  <p
                    className="mt-1 text-[11px] text-[color:var(--text-3)]"
                    data-testid="profile-frequency-decide-later-hint"
                  >
                    You can set this anytime from Autopilot.
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <label className="text-xs text-[color:var(--text-2)] block mb-2">Social platforms</label>
                <div className="flex flex-wrap gap-2">
                  {PLATFORMS.map((p) => {
                    const active = profile.platforms?.includes(p);
                    return (
                      <button
                        key={p}
                        type="button"
                        disabled={!editing}
                        onClick={() => togglePlatform(p)}
                        data-testid={`profile-platform-${p.replace(/[^a-z]/gi, "").toLowerCase()}`}
                        className={
                          "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs border transition-colors " +
                          (active
                            ? "chip-grad"
                            : "bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-2)] hover:text-[color:var(--text-1)]")
                        }
                      >
                        {active ? <CheckCircle2 className="h-3 w-3" /> : null}
                        {p}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Optional business details (collapsible) — preserved 1:1 from Dashboard */}
              <div className="sm:col-span-2 lg:col-span-3 mt-2">
                <button
                  type="button"
                  onClick={() => setShowOptionalDetails((v) => !v)}
                  data-testid="profile-optional-details-toggle"
                  className="w-full flex items-center justify-between gap-3 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]/60 hover:bg-[color:var(--surface-3)] px-4 py-3 text-left transition"
                  aria-expanded={showOptionalDetails}
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-[color:var(--text-1)]">Optional business details</span>
                    <span className="text-[11px] text-[color:var(--text-3)] mt-0.5">
                      The more details you add, the more accurate your posts will be. AI will never invent details you haven&apos;t entered.
                    </span>
                  </div>
                  <ChevronDown className={"h-4 w-4 text-[color:var(--text-3)] transition-transform " + (showOptionalDetails ? "rotate-180" : "")} />
                </button>
                {showOptionalDetails ? (
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="profile-optional-details-fields">
                    <div className="sm:col-span-2 lg:col-span-3">
                      <label className="text-xs text-[color:var(--text-2)]">Short description</label>
                      <Input
                        value={profile.short_description || ""}
                        disabled={!editing}
                        onChange={(e) => setProfile({ ...profile, short_description: e.target.value })}
                        placeholder="Neighborhood pizzeria serving wood-fired pies since 2014"
                        data-testid="profile-short-description"
                        maxLength={300}
                        className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[color:var(--text-2)]">Phone</label>
                      <Input
                        value={profile.phone || ""}
                        disabled={!editing}
                        onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                        data-testid="profile-phone"
                        maxLength={40}
                        className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[color:var(--text-2)]">Business email</label>
                      <Input
                        type="email"
                        value={profile.business_email || ""}
                        disabled={!editing}
                        onChange={(e) => setProfile({ ...profile, business_email: e.target.value })}
                        placeholder="hello@yourbusiness.com"
                        data-testid="profile-business-email"
                        maxLength={120}
                        className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[color:var(--text-2)]">Website</label>
                      <Input
                        value={profile.website_url || ""}
                        disabled={!editing}
                        onChange={(e) => setProfile({ ...profile, website_url: e.target.value })}
                        placeholder="https://yourbusiness.com"
                        data-testid="profile-website-url"
                        maxLength={300}
                        className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[color:var(--text-2)]">Booking / appointment link</label>
                      <Input
                        value={profile.booking_url || ""}
                        disabled={!editing}
                        onChange={(e) => setProfile({ ...profile, booking_url: e.target.value })}
                        placeholder="https://calendly.com/yourbusiness"
                        data-testid="profile-booking-url"
                        maxLength={300}
                        className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[color:var(--text-2)]">Preferred CTA</label>
                      <Select
                        value={profile.preferred_cta || ""}
                        disabled={!editing}
                        onValueChange={(v) => setProfile({ ...profile, preferred_cta: v === "__none__" ? "" : v })}
                      >
                        <SelectTrigger className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]" data-testid="profile-preferred-cta">
                          <SelectValue placeholder="No preference — let AI decide" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">No preference — let AI decide</SelectItem>
                          <SelectItem value="Call now">Call now</SelectItem>
                          <SelectItem value="Book online">Book online</SelectItem>
                          <SelectItem value="Visit our website">Visit our website</SelectItem>
                          <SelectItem value="Stop in today">Stop in today</SelectItem>
                          <SelectItem value="Message us">Message us</SelectItem>
                          <SelectItem value="Get a quote">Get a quote</SelectItem>
                          <SelectItem value="Schedule appointment">Schedule appointment</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-2 lg:col-span-3">
                      <label className="text-xs text-[color:var(--text-2)]">Address</label>
                      <Input
                        value={profile.address || ""}
                        disabled={!editing}
                        onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                        placeholder="123 Main St, Brooklyn, NY 11201"
                        data-testid="profile-address"
                        maxLength={300}
                        className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                      />
                    </div>
                    <div className="sm:col-span-2 lg:col-span-3">
                      <label className="text-xs text-[color:var(--text-2)]">Hours of operation</label>
                      <Input
                        value={profile.hours_of_operation || ""}
                        disabled={!editing}
                        onChange={(e) => setProfile({ ...profile, hours_of_operation: e.target.value })}
                        placeholder="Mon–Fri 8a–8p · Sat–Sun 9a–9p"
                        data-testid="profile-hours"
                        maxLength={500}
                        className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                      />
                    </div>
                    <div className="sm:col-span-2 lg:col-span-3">
                      <label className="text-xs text-[color:var(--text-2)]">Services / products offered</label>
                      <Input
                        value={profile.services_offered || ""}
                        disabled={!editing}
                        onChange={(e) => setProfile({ ...profile, services_offered: e.target.value })}
                        placeholder="Wood-fired pizza, fresh pasta, weekly specials, catering"
                        data-testid="profile-services"
                        maxLength={800}
                        className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                      />
                    </div>
                    <div className="sm:col-span-2 lg:col-span-3">
                      <label className="text-xs text-[color:var(--text-2)]">Target customers</label>
                      <Input
                        value={profile.target_customers || ""}
                        disabled={!editing}
                        onChange={(e) => setProfile({ ...profile, target_customers: e.target.value })}
                        placeholder="Families, students, professionals in the neighborhood"
                        data-testid="profile-target-customers"
                        maxLength={400}
                        className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                      />
                    </div>
                    <div className="sm:col-span-2 lg:col-span-3 text-[11px] text-[color:var(--text-3)]">
                      Postmoro will only use these details to personalize your content. You can edit or remove them anytime.
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          )}
          {!profileComplete && !editing && !loading ? (
            <div className="mt-4 p-3 rounded-md bg-[color:rgba(255,176,32,0.06)] border border-[color:rgba(255,176,32,0.25)] text-xs text-[color:var(--warning)] flex items-center gap-2" data-testid="profile-complete-warning">
              <TrendingUp className="h-3.5 w-3.5" />
              Complete your profile so AI captions match your brand.
            </div>
          ) : null}
        </GlassCard>

        {/* Content Restrictions & Review — optional, off by default.
            Purely a prompt-shaping preference. Does not gate publishing. */}
        {!loading && profile ? (
          <ContentRestrictionsSection
            initial={profile.content_restrictions}
            onSaved={(fresh) => {
              if (fresh) setProfile(fresh);
            }}
          />
        ) : null}

        {/* Email notifications — one user-toggle for successful publish
            emails; billing and failure are always on. Backend endpoints
            already handle both loading and saving. */}
        <EmailNotificationsSection />

        <div className="mt-4 text-[11px] text-[color:var(--text-3)]">
          <Link to="/dashboard" className="hover:underline" data-testid="business-profile-back-link">
            ← Back to dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}
